// WU-2.1 — compose dialog. Fields: message (textarea, 500 budget), level,
// audience (Everyone / All supervisors / Specific user → recipient picker
// fed by GET /users), optional expiry hours. Mirrors CreateBlockDialog.
import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { useVisibleTeams } from "@/hooks/useVisibleTeams";
import {
  announcementsApi,
  type AnnouncementAudience,
  type AnnouncementLevel,
} from "../api/client";
import styles from "../announcements.module.css";

interface TenantUser { id: string; name: string; email: string; isActive: boolean; }

export function ComposeAnnouncementDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [body, setBody] = useState("");
  const [level, setLevel] = useState<AnnouncementLevel>("info");
  const [audienceType, setAudienceType] = useState<AnnouncementAudience>("tenant");
  const [audienceUserId, setAudienceUserId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [expiresInHours, setExpiresInHours] = useState<string>("");
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { teams } = useVisibleTeams();

  useEffect(() => {
    if (audienceType !== "user" || users.length > 0) return;
    api.get<{ users: TenantUser[] }>("/users")
      .then((res) => setUsers(res.users.filter((u) => u.isActive)))
      .catch(() => setError("Couldn't load users"));
  }, [audienceType, users.length]);

  const canSubmit =
    body.trim().length > 0 &&
    (audienceType !== "user" || audienceUserId !== "") &&
    (audienceType !== "team" || teamId !== "");

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await announcementsApi.create({
        body: body.trim(),
        level,
        audienceType,
        ...(audienceType === "user" ? { audienceUserId } : {}),
        ...(audienceType === "team" ? { teamId } : {}),
        ...(Number(expiresInHours) > 0 ? { expiresInHours: Number(expiresInHours) } : {}),
      });
      onCreated();
    } catch {
      setError("Failed to post announcement");
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal} role="dialog" aria-label="New announcement">
        <h2>New Announcement</h2>
        <div className={styles.field}>
          <label>
            <span>Message</span>
            <textarea aria-label="Message" maxLength={500} rows={3} value={body}
              onChange={(e) => setBody(e.target.value)} />
          </label>
        </div>
        <div className={styles.field}>
          <label>
            <span>Level</span>
            <select aria-label="Level" value={level} onChange={(e) => setLevel(e.target.value as AnnouncementLevel)}>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
            </select>
          </label>
        </div>
        <div className={styles.field}>
          <label>
            <span>Audience</span>
            <select aria-label="Audience" value={audienceType}
              onChange={(e) => {
                setAudienceType(e.target.value as AnnouncementAudience);
                setAudienceUserId("");
                setTeamId("");
              }}>
              <option value="tenant">Everyone</option>
              <option value="supervisors">All supervisors</option>
              <option value="user">Specific user</option>
              {teams.length > 0 && <option value="team">Team…</option>}
            </select>
          </label>
        </div>
        {audienceType === "user" && (
          <div className={styles.field}>
            <label>
              <span>Recipient</span>
              <select aria-label="Recipient" value={audienceUserId} onChange={(e) => setAudienceUserId(e.target.value)}>
                <option value="">Select a user…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </label>
          </div>
        )}
        {audienceType === "team" && (
          <div className={styles.field}>
            <label>
              <span>Team</span>
              <select aria-label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">— select a team —</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div className={styles.field}>
          <label>
            <span>Expires after (hours, optional)</span>
            <input aria-label="Expires after hours" type="number" min={1} max={720} value={expiresInHours}
              onChange={(e) => setExpiresInHours(e.target.value)} />
          </label>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.modalActions}>
          <button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={!canSubmit || submitting}>Post</button>
        </div>
      </div>
    </div>
  );
}
